/* ============================================
   QUOTE DASHBOARD — Deal Feed "Rep view" / "Everything"
   Asset: dashboard-feed-view.js
   CUSTOM_FEEDVIEW_BY_CLAUDE (Ryan 2026-10-10)
   ============================================
   Since the agents joined the portal, a deal's feed is mostly their notes and the worker's
   automation lines, and a rep scrolling for their own calls, emails and notes cannot find them.
   This module is the ONE place that decides what kind of entry each feed line is, and whether
   the "Rep view" shows it. dashboard-activity-card.js asks it; nothing else should guess.

   WHAT REP VIEW SHOWS (Ryan's picks, 2026-10-10):
     - what a PERSON TYPED in the Activity card's composer: calls, notes, texts, meetings,
       follow-ups (and the status change such a note caused, drawn with it)
     - emails between our team and the customer (the rep's own mailbox), held emails included
     - Abby's emails (orders@ threads) ONLY when the person viewing is on them (from / to / cc)
     - MILESTONES, whoever recorded them, rep or agent, drawn as bold bands
   WHAT IT HIDES: agent notes, the worker's automation lines, AND every note the worker wrote on a
   person's behalf ("Freight requested by RW ...", a cadence hold, a samples hold) plus every bare
   status-change row (second pass, same day: "not relevant to the sales process"). The Rep view /
   Everything switch in the DEAL FEED title is the way to see the rest.

   ⚠️ "TYPED" IS STAMPED FROM 2026-10-11 (`typed: true`, written by /note-entry-add for a person).
   An older entry is judged by its SHAPE: the composer's id ('ne_' + time, never the worker's
   'ne_df_' / 'ne_email_'), a person's code in `by`, not a system type, and not one of the
   sentences the worker's own direct writers compose (AUTO_TEXT_RX). Several worker writers mint
   the composer's id shape, so the text list is what keeps their notes out.

   ⛔ THE FILTER LIVES IN THE BROWSER AND NOWHERE ELSE. The agents read the feed through
   GET /deal-feed and GET /rep-quotes, which never see this module, so Rep view can never hide
   anything from an agent. Do not move this rule into the worker.

   ⚠️ AN AGENT IS RECOGNISED THREE WAYS, because `by` alone cannot say it: the worker stamps
   `viaAgent` on every entry an agent's request writes (since 2026-10-10); older entries carry the
   agent's own code in `by` (AIOPS, ONYX, KAREN...) or `actor` on a system line, or the literal
   'agent' a held-job release writes. The list of agent codes comes from GET /rep-settings
   (`agentCodes`, every seat ever created), cached in localStorage, with a built-in fallback.

   Every page load starts in Rep view, for admins too; Everything lasts until the page reloads
   (Ryan 2026-10-10, revising the first cut, which remembered the choice per person).
   ============================================ */
(function () {
  'use strict';
  window.RQD = window.RQD || {};

  var LS_VIEW = 'rqd-feed-view';
  var LS_AGENTS = 'rqd-feed-agent-codes';
  var FALLBACK_AGENTS = ['AIOPS', 'ONYX', 'KAREN', 'ELON', 'GROK'];
  var ORDERS_RX = /^orders@blueskyfitsupply\.com$/i;
  /* The portal's own mailers ("Freight finalized", "Balance collected"): a notice, not a conversation. */
  var HOUSE_MAILER_RX = /^(notify|noreply|no-reply)@blueskyfitsupply\.com$/i;
  /* `by` values the worker writes for itself that do not start with an underscore. */
  var SYSTEM_BYS = { SYSTEM: 1, ADMIN: 1, UNKNOWN: 1 };

  /* Ryan's milestone list. The icon replaces the type emoji; the label is the band's headline. */
  var MILESTONES = {
    'lead':      { icon: '📥', label: 'Lead came in', tone: 'lead' },
    'quote':     { icon: '📄', label: 'Quote sent', tone: 'quote' },
    'sold':      { icon: '🏆', label: 'Sold', tone: 'sold' },
    'lost':      { icon: '❌', label: 'Lost', tone: 'lost' },
    'cust-po':   { icon: '🧾', label: 'Customer PO received', tone: 'custpo' },
    'vendor-po': { icon: '📦', label: 'Vendor PO sent', tone: 'vendorpo' },
    'ordered':   { icon: '📦', label: 'Ordered from supplier', tone: 'vendorpo' },
    'invoice':   { icon: '💵', label: 'Invoice sent', tone: 'invoice' },
    'payment':   { icon: '💰', label: 'Payment received', tone: 'payment' },
    'shipped':   { icon: '🚚', label: 'Shipped', tone: 'shipped' },
    'delivered': { icon: '✅', label: 'Delivered', tone: 'delivered' },
    'installed': { icon: '✅', label: 'Installed', tone: 'delivered' },
    'closed':    { icon: '🔒', label: 'Deal closed', tone: 'closed' },
  };

  /* Entries written after this carry `typed` when a person typed them; absence then means no. */
  var TYPED_STAMP_SINCE = Date.parse('2026-10-11T00:00:00Z');
  /* The sentences the worker's direct writers compose with a person's code in `by` and the
     composer's id shape (outlook-reply cadence hold, the samples hold, the hold dialog's default,
     the phone-intake hold, lib/lead-hold.js, a pending's follow-up). */
  var AUTO_TEXT_RX = /^(Follow-up reminder|Samples shipped — 7-day|Put on hold until \d|Spoke on the phone at intake)|Holding until \d{4}-\d{2}-\d{2}\.$/;

  var state = { view: 'rep', agents: {}, fetched: false };

  function setAgents(list) {
    var map = {};
    FALLBACK_AGENTS.concat(Array.isArray(list) ? list : []).forEach(function (c) {
      var k = String(c || '').trim().toUpperCase();
      if (k) map[k] = 1;
    });
    state.agents = map;
  }
  /* ⛔ EVERY PAGE LOAD STARTS IN REP VIEW (Ryan 2026-10-10, same day: "rep view always is the
     default on page load/refresh"). Everything is a look for this page only; nothing is remembered,
     in this browser or on the server. Clear the old remembered choice so it cannot come back. */
  try { localStorage.removeItem(LS_VIEW); } catch (e) { /* ignore */ }
  try {
    setAgents(JSON.parse(localStorage.getItem(LS_AGENTS) || 'null'));
  } catch (e) { setAgents(null); }

  function typesOf(e) {
    if (Array.isArray(e.types) && e.types.length) return e.types;
    return e.type ? [e.type] : [];
  }
  function dedupOf(e) { return typeof e._df_dedup === 'string' ? e._df_dedup : ''; }

  /* Which milestone an entry is, or null. Keyed on the TYPES the worker writes and, for the
     shipment family, on the dedup key, because `vendor-shipped` also covers "tracking link
     added", "customer marked told" and a supplier's "it shipped" heads-up, none of which is the
     shipment itself. */
  function milestoneOf(e) {
    if (!e || e.deletedAt || e._legacy) return null;
    if (e._synthOrigin) return 'lead'; // the start-of-deal band built for a deal with no lead entry
    var t = typesOf(e);
    var has = function (k) { return t.indexOf(k) !== -1; };
    if (has('lead-intake') || /^ne_df_intake_/.test(String(e.id || ''))) return 'lead';
    if (e.transition && (e.type === 'system' || has('system'))) {
      var to = String(e.transition.to || '').toLowerCase();
      if (to === 'sold') return 'sold';
      if (to === 'lost') return 'lost';
    }
    if (has('quote-sent')) return 'quote';
    if (has('po-received')) return 'cust-po';
    if (has('vendor-po') || has('po-sent')) return 'vendor-po';
    if (has('vendor-purchased') || has('vendor-web-purchased')) return 'ordered';
    if (has('invoice-sent')) return 'invoice';
    if (has('payment')) return 'payment';
    if (has('install-done')) return 'installed';
    if (has('deal-closed')) return 'closed';
    var d = dedupOf(e);
    if (/^(delivered|received):/.test(d)) return 'delivered';
    if (has('vendor-shipped')) {
      if (e.trackingStamp || /^(tracking-stamp|vendor-shipped):/.test(d) || /^pickup-stamp:.*:confirmed$/.test(d)) return 'shipped';
    }
    return null;
  }

  function isAgentEntry(e) {
    if (!e) return false;
    if (e.viaAgent || e.actor) return true;
    var by = String(e.by || '').trim().toUpperCase();
    return by === 'AGENT' || !!state.agents[by];
  }
  function isSystemBy(by) {
    var b = String(by || '').trim();
    return b.charAt(0) === '_' || !!SYSTEM_BYS[b.toUpperCase()];
  }

  /* Did a person type this in the composer? */
  function isTyped(e) {
    if (!e) return false;
    if (e.typed === true) return true;
    var t = Date.parse(e.ts || '');
    if (isFinite(t) && t >= TYPED_STAMP_SINCE) return false;
    var id = String(e.id || '');
    if (id.indexOf('ne_') !== 0 || id.indexOf('ne_df_') === 0 || id.indexOf('ne_email_') === 0) return false;
    if (isSystemBy(e.by) || isAgentEntry(e)) return false;
    if (e.type === 'system' || typesOf(e).indexOf('system') !== -1) return false;
    return !AUTO_TEXT_RX.test(String(e.text || '').trim());
  }
  /* A rep's held email (pinned until it sends) is an email, whoever's writer logged it. */
  function isHeldEmail(e) {
    return !!(e.heldMeta || /^held-email:/.test(dedupOf(e))) && !isSystemBy(e.by) && !isAgentEntry(e);
  }

  function mailAddrs(em) {
    var out = [];
    [em.from].concat(Array.isArray(em.to) ? em.to : [], Array.isArray(em.cc) ? em.cc : []).forEach(function (a) {
      if (a) out.push(String(a).trim().toLowerCase());
    });
    return out;
  }
  /* An email on Abby's side of the house: sent BY orders@, or written TO orders@. A rep's own
     email that merely copies orders@ is still the rep's email. */
  function isAbbyMail(e) {
    var em = e.emailMeta || {};
    if (e.by === '_ABBY' || em.systemSend) return true;
    if (ORDERS_RX.test(String(em.from || '').trim())) return true;
    if (em.direction === 'inbound') {
      return [].concat(em.to || [], em.cc || []).some(function (a) { return ORDERS_RX.test(String(a || '').trim()); });
    }
    return false;
  }
  function viewerEmail() {
    try {
      var s = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession();
      return String((s && s.email) || '').trim().toLowerCase();
    } catch (e) { return ''; }
  }

  /* { kind, show, agent, milestone? } — `show` is what Rep view does with it. */
  function classify(e) {
    if (!e) return { kind: 'activity', show: true, agent: false };
    var agent = isAgentEntry(e);
    var m = milestoneOf(e);
    if (m) return { kind: 'milestone', milestone: m, show: true, agent: agent };
    if (e._legacy) return { kind: 'activity', show: true, agent: false };
    if (e.type === 'email' && e.emailMeta) {
      if (HOUSE_MAILER_RX.test(String(e.emailMeta.from || '').trim())) return { kind: 'system', show: false, agent: false };
      /* A [TEST] copy, or Ryan's reply on one: approval plumbing, not the customer conversation. */
      if (/\[TEST(?:\s+\d{1,6})?\]/i.test(String(e.emailMeta.subject || ''))) return { kind: 'system', show: false, agent: false };
      if (isAbbyMail(e)) {
        var me = viewerEmail();
        return { kind: 'abby-email', show: !!me && mailAddrs(e.emailMeta).indexOf(me) !== -1, agent: false };
      }
      return { kind: 'email', show: true, agent: false };
    }
    if (isHeldEmail(e)) return { kind: 'email', show: true, agent: false };
    if (agent) return { kind: 'agent', show: false, agent: true };
    if (isTyped(e)) return { kind: 'activity', show: true, agent: false };
    /* Written FOR somebody, not typed by them: a bare status change or an automatic note. */
    return { kind: e.transition ? 'status' : 'system', show: false, agent: false };
  }

  function isRepView() { return state.view === 'rep'; }

  function toggleHtml() {
    var rep = isRepView();
    return '<span class="rqd-feed-view" role="group" aria-label="Deal Feed view">'
      + '<button type="button" class="rqd-feed-view__btn' + (rep ? ' is-on' : '') + '" data-feed-view="rep" aria-pressed="' + rep + '">Rep view</button>'
      + '<button type="button" class="rqd-feed-view__btn' + (rep ? '' : ' is-on') + '" data-feed-view="all" aria-pressed="' + !rep + '">Everything</button>'
      + '</span>';
  }

  function paintToggles() {
    var rep = isRepView();
    var btns = document.querySelectorAll('.rqd-feed-view__btn');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-feed-view') === 'rep') === rep;
      btns[i].classList.toggle('is-on', on);
      btns[i].setAttribute('aria-pressed', String(on));
    }
  }
  /* Redraw every open deal's entries list (the composer is a sibling subtree, so an unsaved note
     is never touched). A list holding an open inline edit is skipped by repaintEntriesFor itself
     and picks the view up on its next render. */
  function repaintOpenFeeds() {
    if (!window.RQD.repaintEntriesFor) return;
    var cards = document.querySelectorAll('.rqd-notes-log[data-ref]');
    var seen = {};
    for (var i = 0; i < cards.length; i++) {
      var ref = cards[i].getAttribute('data-ref');
      if (!ref || seen[ref]) continue;
      seen[ref] = 1;
      try { window.RQD.repaintEntriesFor(ref); } catch (e) { /* next full render catches up */ }
    }
  }

  function setView(v, opts) {
    if (v !== 'rep' && v !== 'all') return;
    var changed = state.view !== v;
    state.view = v;
    paintToggles();
    if (changed) repaintOpenFeeds();
  }

  document.addEventListener('click', function (ev) {
    var btn = ev.target && ev.target.closest ? ev.target.closest('[data-feed-view]') : null;
    if (!btn) return;
    ev.preventDefault();
    /* ⛔ CAPTURE PHASE, AND THE CLICK STOPS HERE (Ryan 2026-10-10: "it does nothing until I
       refresh"). The deal table's own handlers stop many clicks inside an open row from bubbling,
       so a bubbling listener on document may never hear this one, and a click that did bubble
       could also reach the row's expand / collapse handler. */
    ev.stopPropagation();
    /* The hidden-count line sits INSIDE the entries list, and repaintEntriesFor refuses to
       redraw a list that holds the focused element. */
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (e) { /* ignore */ }
    setView(btn.getAttribute('data-feed-view'));
  }, true);

  /* The agent list, once per page (the view itself is never read back: see the top). */
  function fetchSettings(attempt) {
    try {
      if (!(window.RepPortal && window.RepPortal.API && window.RepPortal.API.get)) {
        if ((attempt || 0) < 5) setTimeout(function () { fetchSettings((attempt || 0) + 1); }, 1500);
        return;
      }
      window.RepPortal.API.get('/rep-settings').then(function (s) {
        state.fetched = true;
        if (!s) return;
        if (Array.isArray(s.agentCodes)) {
          setAgents(s.agentCodes);
          try { localStorage.setItem(LS_AGENTS, JSON.stringify(s.agentCodes)); } catch (e) { /* ignore */ }
        }
        repaintOpenFeeds(); // the agent list may have grown since the cached copy
      }).catch(function () { /* not signed in yet, or offline: the cached agent list stands */ });
    } catch (e) { /* ignore */ }
  }
  fetchSettings(0);

  window.RQD.feedView = {
    classify: classify,
    milestoneOf: milestoneOf,
    milestone: function (key) { return MILESTONES[key] || null; },
    isAgentEntry: isAgentEntry,
    isTyped: isTyped,
    isRepView: isRepView,
    view: function () { return state.view; },
    setView: setView,
    toggleHtml: toggleHtml,
  };
})();
